import { useMemo } from 'react'
import { sanitizeHtml, r2 } from '../../lib/utils'
import CostBreakdown from './CostBreakdown'
import { splitByH4, groupIntoPages } from '../../lib/pdf-layout'
import type { CotizacionTareaCostos, CostoLineItem } from '../../lib/types'

interface Tarea {
  titulo: string
  descripcion_corta?: string
  contenido_detalle?: string
  monto: number
  horas_estimadas?: number | null
  entregables: string[]
  costos?: CotizacionTareaCostos
  costos_items?: CostoLineItem[]
}

interface Props {
  tarea: Tarea
  idx: number
  totalTareas: number
  pc: string
  moneda: string
  empresaNombre: string
  empresaWebsite: string
  clienteRuc: string
  startPage: number
  formatCurrency: (n: number, m: string) => string
  processContent: (html: string) => string
}

export default function TaskDetailPages({
  tarea, idx, totalTareas, pc, moneda, empresaNombre, empresaWebsite,
  clienteRuc, startPage, formatCurrency, processContent,
}: Props) {
  const sections = useMemo(() => splitByH4(tarea.contenido_detalle || ''), [tarea.contenido_detalle])
  const contentPages = useMemo(() => groupIntoPages(sections), [sections])
  const totalMTok = (() => {
    if (tarea.costos_items?.length) {
      const total = tarea.costos_items
        .filter(c => c.unidad === 'MTok')
        .reduce((sum, c) => sum + c.cantidad, 0)
      return total > 0 ? r2(total) : null
    }
    if (!tarea.costos) return null
    const total = tarea.costos.tokens_input_mtok + tarea.costos.tokens_thinking_mtok + tarea.costos.tokens_output_mtok
    return total > 0 ? r2(total) : null
  })()

  const renderPageChrome = (pageNum: number, children: React.ReactNode, isFirst: boolean) => (
    <div
      key={`tarea-${idx}-page-${pageNum}`}
      id={isFirst ? `tarea-${idx}` : undefined}
      className="cotizacion-preview cot-page"
      style={{ marginTop: 24 }}
    >
      <div className="cot-page-header" style={{ background: pc }} />
      <div className="cot-page-ruc">{clienteRuc}</div>
      <div className="cot-body">
        {children}
      </div>
      <div className="cot-footer" style={{ background: pc }}>
        <span>{empresaNombre}</span>
        <span>Pág. {startPage + pageNum}</span>
        <span>{empresaWebsite}</span>
      </div>
    </div>
  )

  const pages: React.ReactNode[] = []
  let pageCounter = 0

  // Distribute content across pages
  contentPages.forEach((pageSections, pageIdx) => {
    const isFirst = pageIdx === 0
    pages.push(
      renderPageChrome(pageCounter, (
        <>
          {isFirst && (
            <>
              <div className="cot-task-nav">
                <a className="cot-task-nav-back" onClick={() => document.getElementById('resumen')?.scrollIntoView({ behavior: 'smooth' })}>◀ Volver al Resumen</a>
                <span className="cot-task-nav-indicator">Tarea {idx + 1} de {totalTareas}</span>
              </div>
              <div className="cot-task-header">
                <div>
                  <div className="cot-task-number">Tarea {idx + 1}</div>
                  <h2 className="cot-task-title">{tarea.titulo}</h2>
                  {tarea.horas_estimadas && <span className="cot-task-horas">{tarea.horas_estimadas} horas estimadas</span>}
                  {totalMTok !== null && <span className="cot-task-horas cot-task-tokens-badge">{totalMTok}M tokens</span>}
                </div>
                <div className="cot-task-monto" style={{ color: pc }}>{formatCurrency(tarea.monto, moneda)}</div>
              </div>
            </>
          )}
          {!isFirst && (
            <div className="cot-task-continuation">
              <span className="cot-task-number">Tarea {idx + 1}</span>
              <span className="cot-task-cont-label"> — {tarea.titulo} (cont.)</span>
            </div>
          )}
          <div className="cot-task-detail" dangerouslySetInnerHTML={{
            __html: sanitizeHtml(processContent(pageSections.map(s => s.html).join('')))
          }} />
        </>
      ), isFirst)
    )
    pageCounter++
  })

  // Final page: Cost breakdown + Entregables
  if (tarea.costos || tarea.entregables.length > 0) {
    const isOnlyPage = contentPages.length === 0
    pages.push(
      renderPageChrome(pageCounter, (
        <>
          {isOnlyPage && (
            <>
              <div className="cot-task-nav">
                <a className="cot-task-nav-back" onClick={() => document.getElementById('resumen')?.scrollIntoView({ behavior: 'smooth' })}>◀ Volver al Resumen</a>
                <span className="cot-task-nav-indicator">Tarea {idx + 1} de {totalTareas}</span>
              </div>
              <div className="cot-task-header">
                <div>
                  <div className="cot-task-number">Tarea {idx + 1}</div>
                  <h2 className="cot-task-title">{tarea.titulo}</h2>
                  {tarea.horas_estimadas && <span className="cot-task-horas">{tarea.horas_estimadas} horas estimadas</span>}
                  {totalMTok !== null && <span className="cot-task-horas cot-task-tokens-badge">{totalMTok}M tokens</span>}
                </div>
                <div className="cot-task-monto" style={{ color: pc }}>{formatCurrency(tarea.monto, moneda)}</div>
              </div>
            </>
          )}
          {!isOnlyPage && (
            <div className="cot-task-continuation">
              <span className="cot-task-number">Tarea {idx + 1}</span>
              <span className="cot-task-cont-label"> — {tarea.titulo} (cont.)</span>
            </div>
          )}
          {tarea.costos && (
            <CostBreakdown costos={tarea.costos} moneda={moneda} />
          )}
          {tarea.entregables.length > 0 && (
            <div className="cot-task-entregables">
              <h4>Entregables</h4>
              <ul>
                {tarea.entregables.map((e, i) => <li key={i}>✓ {e}</li>)}
              </ul>
            </div>
          )}
        </>
      ), contentPages.length === 0)
    )
  }

  return <>{pages}</>
}
